import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { supabaseClient } from "../shared/supabase";
import { Database } from "../database.types";
import { PageTitle } from "../components/common/PageData";

const CenteredPageTitle = styled(PageTitle)`
  text-align: center;
  font-size: 32px;
  font-weight: 700;
  margin-bottom: 20px;
  text-transform: uppercase;
  letter-spacing: 2px;

  @media (max-width: 768px) {
    font-size: 28px;
    letter-spacing: 1px;
  }
`;

type Convention = Database["public"]["Tables"]["conventions"]["Row"];

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 40px 20px;
  background: #fff;
`;

const CategorySection = styled.section`
  margin-bottom: 80px;
`;

const CategoryTitle = styled.h2`
  font-family: "Open Sans", sans-serif;
  font-size: 18px;
  font-weight: 700;
  margin-bottom: 40px;
  color: #000;
  text-transform: uppercase;
  text-align: center;
  letter-spacing: 1px;
`;

const ConventionsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 40px;

  @media (min-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 80px;
  }

  @media (min-width: 1200px) {
    grid-template-columns: repeat(3, 1fr);
    gap: 60px;
  }
`;

const ConventionCard = styled.article`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  cursor: pointer;
  padding: 20px;
  transition: transform 0.2s ease;

  &:hover {
    transform: scale(1.05);
  }
`;

const LogoContainer = styled.div`
  width: 280px;
  height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;

  @media (min-width: 768px) {
    width: 340px;
    height: 240px;
  }

  @media (min-width: 1200px) {
    width: 300px;
    height: 220px;
  }
`;

const Logo = styled.img`
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
`;

const Name = styled.h3`
  font-family: "Open Sans", sans-serif;
  font-size: 16px;
  font-weight: 400;
  margin: 0;
  color: #000;
  text-align: center;
  line-height: 1.4;

  @media (max-width: 768px) {
    font-size: 18px;
  }
`;

const Description = styled.p`
  font-family: "Open Sans", sans-serif;
  font-size: 14px;
  color: #666;
  margin: 8px 0 0 0;
  text-align: center;
  line-height: 1.4;

  @media (max-width: 768px) {
    font-size: 15px;
  }
`;

const LoadingMessage = styled.p`
  text-align: center;
  color: #666;
  opacity: 0.6;
  margin-top: 40px;
`;

const ErrorMessage = styled.p`
  text-align: center;
  color: #ff0000;
  margin-top: 40px;
`;

const EmptyMessage = styled.p`
  text-align: center;
  color: #666;
  margin-top: 40px;
`;

const IntroText = styled.p`
  font-family: "Open Sans", sans-serif;
  font-size: 18px;
  color: #000;
  text-align: center;
  margin: 0 auto 60px;
  max-width: 800px;
  line-height: 1.6;

  @media (max-width: 768px) {
    font-size: 16px;
    margin-bottom: 40px;
  }
`;

export const Conventions: React.FC = () => {
  const [conventions, setConventions] = useState<Convention[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchConventions = async () => {
      try {
        const { data, error } = await supabaseClient
          .from("conventions")
          .select("*")
          .eq("is_active", true)
          .order("category", { ascending: true })
          .order("name", { ascending: true });

        if (error) {
          throw error;
        }

        setConventions(data || []);
      } catch (err) {
        console.error("Error fetching conventions:", err);
        setError("Errore nel caricamento delle convenzioni");
      } finally {
        setLoading(false);
      }
    };

    fetchConventions();
  }, []);

  const handleConventionClick = (convention: Convention) => {
    if (convention.redirect_url) {
      window.open(convention.redirect_url, "_blank");
    }
  };

  if (loading) {
    return (
      <Container>
        <CenteredPageTitle>CONVENZIONI</CenteredPageTitle>
        <IntroText>
          I Nostri partner vi permettono di avere sconti su prodotti e servizi
          presentando la vostra tessera CO2
        </IntroText>
        <LoadingMessage>Caricamento...</LoadingMessage>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <CenteredPageTitle>CONVENZIONI</CenteredPageTitle>
        <IntroText>
          I Nostri partner vi permettono di avere sconti su prodotti e servizi
          presentando la vostra tessera CO2
        </IntroText>
        <ErrorMessage>{error}</ErrorMessage>
      </Container>
    );
  }

  if (conventions.length === 0) {
    return (
      <Container>
        <CenteredPageTitle>CONVENZIONI</CenteredPageTitle>
        <IntroText>
          I Nostri partner vi permettono di avere sconti su prodotti e servizi
          presentando la vostra tessera CO2
        </IntroText>
        <EmptyMessage>Nessuna convenzione disponibile al momento</EmptyMessage>
      </Container>
    );
  }

  // Group conventions by category
  const groupedConventions = conventions.reduce<Record<string, Convention[]>>(
    (acc, convention) => {
      const category = convention.category || "Altro";
      if (!acc[category]) {
        acc[category] = [];
      }
      acc[category].push(convention);
      return acc;
    },
    {},
  );

  return (
    <Container>
      <CenteredPageTitle>CONVENZIONI</CenteredPageTitle>
      <IntroText>
        I Nostri partner vi permettono di avere sconti su prodotti e servizi
        presentando la vostra tessera CO2
      </IntroText>
      {Object.entries(groupedConventions).map(
        ([category, categoryConventions]) => (
          <CategorySection key={category}>
            <CategoryTitle>{category}</CategoryTitle>
            <ConventionsGrid>
              {categoryConventions.map((convention) => (
                <ConventionCard
                  key={convention.id}
                  onClick={() => handleConventionClick(convention)}
                >
                  {convention.image_url && (
                    <LogoContainer>
                      <Logo
                        src={convention.image_url}
                        alt={convention.name}
                        loading="lazy"
                      />
                    </LogoContainer>
                  )}
                  <Name>{convention.name}</Name>
                  {convention.description && (
                    <Description>{convention.description}</Description>
                  )}
                </ConventionCard>
              ))}
            </ConventionsGrid>
          </CategorySection>
        ),
      )}
    </Container>
  );
};
